/** Caps primary pill CTAs (dropzone “Select…”, Transcribe, Start recording, etc.) so they don’t span the full panel. */
export const SCRIBE_PRIMARY_CTA_MAX_WIDTH_PX = 440;

export const scribePrimaryCtaContainerSx = {
  width: `min(100%, ${SCRIBE_PRIMARY_CTA_MAX_WIDTH_PX}px)`,
  alignSelf: "center",
  mx: "auto",
} as const;

/**
 * #611 — the "＋ New recording" / "＋ New audio file" reset buttons.
 *
 * The theme renders `variant="outlined"` as grey `#949BB8` text on a
 * `rgba(255,255,255,0.11)` border, which is visually indistinguishable from
 * MUI's disabled styling — the reporter read an enabled button as "greyed out".
 * These buttons carry the teal accent so they read as clickable, and keep the
 * standard muted treatment when they genuinely ARE disabled (mid-upload).
 */
export const scribeResetButtonSx = {
  color: "var(--teal)",
  borderColor: "rgba(0,229,195,0.45)",
  "&:hover": {
    borderColor: "var(--teal)",
    backgroundColor: "rgba(0,229,195,0.08)",
  },
  "&.Mui-disabled": {
    color: "#5A6080",
    borderColor: "rgba(255,255,255,0.11)",
  },
} as const;

/** Stable identifiers for the tools on the Scribe page (tabs, phone "Tool" dropdown and panels). */
export type TabId = "record" | "audio" | "document" | "docChat" | "paste" | "qa" | "formFiller";

/**
 * Tools shown on the Scribe page, in display order. Only ids listed here render.
 * Document, Doc Chat and Paste return in a later release; Q&A and Form Filler are
 * hidden per Lloyd's 21 Oct 2026 handover. The hidden tabs and their actions are kept
 * in the codebase: add an id back to this list to show it again.
 */
export const SCRIBE_VISIBLE_TABS: TabId[] = ["record", "audio"];

// --- AIS-4: vocabulary panel ---------------------------------------------------

/** Legal vocabulary preset sent as `vocabulary` with every transcription while the panel is hidden. */
export const LEGAL_VOCAB_PRESET =
  "plaintiff, defendant, respondent, applicant, deponent, hereof, thereof, " +
  "whereof, herein, therein, hereunder, thereunder, hereby, thereby, hereto, " +
  "thereto, inter alia, mutatis mutandis, prima facie, bona fide, proviso, " +
  "SASRIA, FAIS, POPIA, NaFOS, HPCSA, STSMA";

/**
 * Show the "Vocabulary & Term Guide" panel on the Record and Audio tabs. Hidden per
 * Alex's 21 Oct 2026 decision: every transcription uses the legal preset. When true,
 * the panel returns and the typed vocabulary is sent instead.
 */
export const SHOW_VOCABULARY_PANEL: boolean = false;
